import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Select, 
  Tag, 
  Space, 
  message,
  Divider,
  Modal,
  Row,
  Col
} from 'antd';
import { 
  SaveOutlined, 
  EyeOutlined, 
  SendOutlined,
  CloseOutlined,
  PlusOutlined,
  TagOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { posts } from '../../api/mockData';
import './index.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const WritePostPage = () => {
  const [form] = Form.useForm();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // 检查用户登录状态
    const userStr = localStorage.getItem('currentUser');
    if (!userStr) {
      message.warning('请先登录后写文章');
      navigate('/auth');
      return;
    }
    
    setCurrentUser(JSON.parse(userStr));
    
    // 检查是否是编辑模式
    const editId = searchParams.get('edit');
    if (editId) {
      const postToEdit = posts.find(p => p.id === editId);
      if (postToEdit) {
        // 检查权限
        if (postToEdit.authorId !== JSON.parse(userStr).id) {
          message.error('您没有权限编辑这篇文章');
          navigate('/');
          return;
        }
        
        setIsEditing(true);
        form.setFieldsValue({
          title: postToEdit.title,
          summary: postToEdit.summary,
          tags: postToEdit.tags,
          category: postToEdit.category
        });
        setContent(postToEdit.content);
      }
    }
  }, [form, navigate, searchParams]);

  // 所有可用的标签
  const allTags = ['React', 'JavaScript', 'Java', 'Linux', '前端', '后端', '数据库', 
    '算法', '美食', '旅行', '生活', '学习', '教程', '分享', '问题解决'];

  // 分类选项
  const categories = ['技术', '生活', '旅行', '美食', '学习', '其他'];

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleSubmit = async (values) => {
  if (!content.trim()) {
    message.warning('请输入文章内容');
    return;
  }

  setLoading(true);
  
  try {
    // 模拟 API 调用延迟
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (isEditing) {
      // 编辑文章逻辑
      message.success('文章更新成功！');
    } else {
      // 新建文章逻辑
      const newPost = {
        id: Date.now().toString(),
        title: values.title,
        author: currentUser.name,
        authorId: currentUser.id,
        date: new Date().toISOString().replace('T', ' ').substring(0, 19),
        tags: values.tags,
        category: values.category,
        summary: values.summary,
        content: content,
        readCount: 0,
        likes: 0
      };
      
      // 将新文章添加到模拟数据中
      posts.push(newPost);
      
      // 在实际应用中，这里应该调用 API 保存文章
      message.success('文章发布成功！');
      form.resetFields();
      setContent('');
    }
    
    navigate('/');
  } catch (error) {
    message.error('操作失败，请重试');
    console.error('Submit error:', error);
  } finally {
    setLoading(false);
  }
};

  const handlePreview = () => {
    if (!form.getFieldValue('title')) {
      message.warning('请输入文章标题');
      return;
    }
    setPreviewVisible(true);
  };

  const handleSaveDraft = () => {
    const values = form.getFieldsValue();
    const draft = {
      title: values.title || '未命名草稿',
      summary: values.summary || '',
      tags: values.tags || [],
      category: values.category || '技术',
      content: content,
      lastSaved: new Date().toISOString()
    };
    
    localStorage.setItem('blog_draft', JSON.stringify(draft));
    message.success('草稿已保存到本地');
  };

  const handleLoadDraft = () => {
    const draftStr = localStorage.getItem('blog_draft');
    if (draftStr) {
      const draft = JSON.parse(draftStr);
      form.setFieldsValue({
        title: draft.title === '未命名草稿' ? '' : draft.title,
        summary: draft.summary,
        tags: draft.tags,
        category: draft.category
      });
      setContent(draft.content);
      message.success('草稿已加载');
    } else {
      message.info('没有找到草稿');
    }
  };

  if (!currentUser) {
    return (
      <div className="write-page">
        <div className="write-container">
          <Card className="no-login">
            <div className="login-prompt">
              <FileTextOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '24px' }} />
              <Title level={3}>请先登录</Title>
              <Text type="secondary" style={{ marginBottom: '32px' }}>
                登录后即可撰写和发布文章
              </Text>
              <Space>
                <Button type="primary" onClick={() => navigate('/auth')}>
                  立即登录
                </Button>
                <Button onClick={() => navigate('/')}>
                  返回首页
                </Button>
              </Space>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="write-page">
      <div className="write-container">
        <div className="write-header">
          <Title level={2} className="write-title">
            <FileTextOutlined /> {isEditing ? '编辑文章' : '写文章'}
          </Title>
          <Text className="write-subtitle">
            {isEditing ? '修改您的文章内容' : '分享您的知识与见解'}
          </Text>
        </div>
        
        <Card className="write-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              category: '技术'
            }}
          >
            {/* 基本信息部分 */}
            <div className="form-section">
              <Title level={4} className="section-title">基本信息</Title>
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    name="title"
                    label="文章标题"
                    rules={[
                      { required: true, message: '请输入文章标题' },
                      { min: 5, message: '标题至少5个字符' },
                      { max: 100, message: '标题最多100个字符' }
                    ]}
                  >
                    <Input 
                      placeholder="请输入文章标题"
                      className="title-input"
                    />
                  </Form.Item>
                </Col>
                
                <Col span={24}>
                  <Form.Item
                    name="summary"
                    label="文章摘要"
                    rules={[
                      { required: true, message: '请输入文章摘要' },
                      { min: 20, message: '摘要至少20个字符' },
                      { max: 200, message: '摘要最多200个字符' }
                    ]}
                  >
                    <TextArea 
                      placeholder="请输入文章摘要"
                      className="summary-textarea"
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tags"
                    label="文章标签"
                    rules={[
                      { required: true, message: '请选择至少一个标签' }
                    ]}
                  >
                    <Select
                      mode="tags"
                      placeholder="选择或输入标签"
                      className="tag-select"
                      suffixIcon={<TagOutlined />}
                      tagRender={(props) => (
                        <Tag 
                          {...props}
                          color="blue"
                          closable={props.closable}
                        >
                          {props.label}
                        </Tag>
                      )}
                    >
                      {allTags.map(tag => (
                        <Option key={tag} value={tag}>{tag}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    name="category"
                    label="文章分类"
                    rules={[
                      { required: true, message: '请选择分类' }
                    ]}
                  >
                    <Select
                      placeholder="选择分类"
                      className="category-select"
                    >
                      {categories.map(category => (
                        <Option key={category} value={category}>{category}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>
            
            <Divider />
            
            {/* 内容编辑部分 */}
            <div className="form-section">
              <Title level={4} className="section-title">文章内容</Title>
              <div className="editor-container">
                <div className="editor-toolbar">
                  <Space>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '# 标题\n')}
                    >
                      H1
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '## 二级标题\n')}
                    >
                      H2
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '**加粗文本**')}
                    >
                      B
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '*斜体文本*')}
                    >
                      I
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '> 引用文本\n')}
                    >
                      引用
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '```\n代码块\n```\n')}
                    >
                      代码
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '- 列表项\n')}
                    >
                      列表
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setContent(content + '![](图片链接)')}
                    >
                      图片
                    </Button>
                  </Space>
                </div>
                <TextArea
                  className="editor-content"
                  placeholder="开始撰写您的文章..."
                  value={content}
                  onChange={handleContentChange}
                  autoSize={{ minRows: 20 }}
                />
              </div>
              
              <div style={{ marginTop: '16px' }}>
                <Space>
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />}
                    onClick={handleLoadDraft}
                  >
                    加载草稿
                  </Button>
                  <Button 
                    type="dashed" 
                    icon={<SaveOutlined />}
                    onClick={handleSaveDraft}
                  >
                    保存草稿
                  </Button>
                </Space>
              </div>
            </div>
            
            <Divider />
            
            {/* 操作按钮 */}
            <div className="form-actions">
              <div>
                <Text type="secondary">
                  字数统计: {content.length} 字符
                </Text>
              </div>
              
              <div className="action-buttons">
                <Space>
                  <Button 
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                  >
                    预览
                  </Button>
                  <Button 
                    icon={<CloseOutlined />}
                    onClick={() => navigate('/')}
                  >
                    取消
                  </Button>
                  <Button 
                    type="primary"
                    icon={isEditing ? <SaveOutlined /> : <SendOutlined />}
                    htmlType="submit"
                    loading={loading}
                  >
                    {isEditing ? '更新文章' : '发布文章'}
                  </Button>
                </Space>
              </div>
            </div>
          </Form>
        </Card>
      </div>

      {/* 预览模态框 */}
      <Modal
        title="文章预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={() => {
              form.submit();
              setPreviewVisible(false);
            }}
          >
            确认发布
          </Button>
        ]}
      >
        <div className="preview-section">
          <Title level={2}>{form.getFieldValue('title')}</Title>
          
          <div className="preview-meta" style={{ marginBottom: '24px' }}>
            <Space>
              <Text type="secondary">作者: {currentUser.name}</Text>
              <Text type="secondary">分类: {form.getFieldValue('category')}</Text>
              <div>
                {form.getFieldValue('tags')?.map(tag => (
                  <Tag key={tag} color="blue" style={{ margin: '2px' }}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </Space>
          </div>
          
          <Text strong style={{ display: 'block', marginBottom: '16px' }}>
            {form.getFieldValue('summary')}
          </Text>
          
          <Divider />
          
          <div 
            className="preview-content"
            dangerouslySetInnerHTML={{ 
              __html: content.replace(/\n/g, '<br>')
                .replace(/# (.*?)\n/g, '<h1>$1</h1>')
                .replace(/## (.*?)\n/g, '<h2>$1</h2>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/> (.*?)\n/g, '<blockquote>$1</blockquote>')
                .replace(/```\n([\s\S]*?)\n```/g, '<pre><code>$1</code></pre>')
                .replace(/!\[(.*?)\]\((.*?)\)/g, '<img alt="$1" src="$2" />')
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default WritePostPage;